import * as React from "react"
import { cn } from "@garagem/ui/lib/utils"
import { Check, ChevronsUpDown } from "lucide-react"
import { Button } from "../button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command"
import { Popover, PopoverContent, PopoverTrigger } from "../popover"
import { Input } from "../input"
import {
  AsYouType,
  parsePhoneNumber,
  type CountryCode,
} from "libphonenumber-js"
import { parsePhoneWithDDI } from "../../lib/phone"
import {
  PHONE_COUNTRIES,
  phoneCountrySearchValue,
  type PhoneCountry,
} from "../../lib/phone-countries"

/**
 * PhoneInput is an international phone number input component with country selector.
 *
 * Features:
 * - Country selector with flag and dial code
 * - Automatic formatting powered by libphonenumber-js (Google's metadata)
 * - Returns value in E.164 format (+<dialCode><number>) via onChange
 * - Supports controlled and uncontrolled usage (value, defaultValue)
 * - Auto-selects country by DDI in initial value (defaultValue)
 * - Simple variant without country selector
 * - Accessible and keyboard-friendly
 *
 * Props:
 * - value?: string (controlled value, expects E.164 format: +<dialCode><number>)
 * - defaultValue?: string (initial value, expects E.164 format: +<dialCode><number>)
 * - onChange?: (value: string, country?: Country) => void (E.164 value)
 * - error?: boolean
 * - variant?: 'default' | 'simple'
 * - ...props: All native input props
 */

type Country = PhoneCountry
const countries = PHONE_COUNTRIES

function toLibCountryCode(code: string): CountryCode {
  if (code === "UK") return "GB"
  return code as CountryCode
}

function fromLibCountryCode(code: CountryCode): string {
  if (code === "GB") return "UK"
  return code
}

function getMaxNationalDigits(_countryCode: string): number {
  return 15
}

function formatPhoneNumber(numbers: string, country: Country): string {
  if (!numbers) return ""
  const formatter = new AsYouType(toLibCountryCode(country.code))
  formatter.input(`+${country.dialCode}${numbers}`)
  const formatted = formatter.getNumber()?.formatNational() ?? numbers
  return formatted
}

/**
 * Resolve a PhoneInput country from dial code + national digits.
 * Shared DDIs (NANP +1) must not collapse to the first list match (US).
 */
function resolveCountry(
  ddi: string | null,
  nationalNumber: string,
  preferred: Country,
): Country {
  if (!ddi) return preferred

  if (nationalNumber) {
    try {
      const parsed = parsePhoneNumber(`+${ddi}${nationalNumber}`)
      if (parsed.country) {
        const match = countries.find(
          (c) => c.code === fromLibCountryCode(parsed.country!),
        )
        if (match) return match
      }
    } catch {
      /* fall through */
    }
  }

  // Keep the user's picker choice while metadata cannot decide yet.
  if (preferred.dialCode === ddi) return preferred

  return countries.find((c) => c.dialCode === ddi) ?? preferred
}

/**
 * Strip an accidental dial-code prefix from national input without eating
 * legitimate nationals that share those digits (e.g. Cuban mobiles 53xxxxxx).
 */
function stripAccidentalDialCodePrefix(
  numbers: string,
  country: Country,
): string {
  const dial = country.dialCode
  if (!numbers.startsWith(dial) || numbers.length <= dial.length) {
    return numbers
  }

  const withoutDial = numbers.slice(dial.length)
  if (!withoutDial) return numbers

  try {
    const parsed = parsePhoneNumber(numbers, toLibCountryCode(country.code))
    // Digits were kept as the national number — do not strip.
    if (parsed.nationalNumber === numbers) {
      return numbers
    }
    // libphonenumber consumed the dial code from the digit string (paste/type
    // of DDI + national into the national field).
    if (
      parsed.countryCallingCode === dial &&
      parsed.nationalNumber === withoutDial
    ) {
      return withoutDial
    }
  } catch {
    /* incomplete / ambiguous — keep digits */
  }

  return numbers
}

interface PhoneInputProps extends Omit<
  React.ComponentProps<"input">,
  "type" | "value" | "defaultValue" | "onChange"
> {
  error?: boolean
  value?: string
  defaultValue?: string
  onChange?: (value: string, country?: Country) => void
  variant?: "default" | "simple"
}

function PhoneInput({
  className = "",
  error,
  value,
  defaultValue,
  onChange,
  variant = "default",
  ...props
}: PhoneInputProps) {
  const [open, setOpen] = React.useState(false)
  const [selectedCountry, setSelectedCountry] = React.useState<Country>(
    countries[0]!,
  )
  const [phoneValue, setPhoneValue] = React.useState("")
  const isControlled = value !== undefined

  React.useEffect(() => {
    if (!isControlled && defaultValue) {
      const { ddi, number } = parsePhoneWithDDI(defaultValue)
      const country = resolveCountry(ddi, number ?? "", countries[0]!)
      setSelectedCountry(country)
      setPhoneValue(formatPhoneNumber(number ?? "", country))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (isControlled && value !== undefined) {
      const { ddi, number } = parsePhoneWithDDI(value)
      if (ddi) {
        const found = resolveCountry(ddi, number ?? "", selectedCountry)
        if (found.code !== selectedCountry.code) {
          setSelectedCountry(found)
        }
      }
    }
  }, [isControlled, value, selectedCountry])

  React.useEffect(() => {
    if (isControlled && value !== undefined) {
      const { ddi, number } = parsePhoneWithDDI(value)
      const countryToUse = resolveCountry(ddi, number ?? "", selectedCountry)
      setPhoneValue(formatPhoneNumber(number ?? "", countryToUse))
    }
  }, [isControlled, value, selectedCountry])

  const handleCountryChange = (countryCode: string) => {
    const country = countries.find((c) => c.code === countryCode)
    if (!country) return

    setSelectedCountry(country)
    setOpen(false)

    const digits = phoneValue.replace(/[^\d]/g, "")
    const maxDigits = getMaxNationalDigits(country.code)
    const truncated = digits.slice(0, maxDigits)

    const newValue = truncated ? formatPhoneNumber(truncated, country) : ""

    setPhoneValue(newValue)
    if (!truncated) {
      onChange?.("", country)
      return
    }
    onChange?.(`+${country.dialCode}${truncated}`, country)
  }

  const applyPhoneValue = (digits: string, country: Country) => {
    const maxDigits = getMaxNationalDigits(country.code)
    const truncated = digits.slice(0, maxDigits)

    const formatter = new AsYouType(toLibCountryCode(country.code))
    const formatted = formatter.input(`+${country.dialCode}${truncated}`)
    const nationalFormatted = formatter.getNumber()?.formatNational()

    setPhoneValue(
      nationalFormatted ?? formatted.replace(`+${country.dialCode}`, "").trim(),
    )
    if (!truncated) {
      onChange?.("", country)
      return
    }
    onChange?.(`+${country.dialCode}${truncated}`, country)
  }

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    const numbers = stripAccidentalDialCodePrefix(
      e.currentTarget.value.replace(/[^\d]/g, ""),
      selectedCountry,
    )
    applyPhoneValue(numbers, selectedCountry)
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text")
    if (!pasted) return

    const hasPlus = pasted.trim().startsWith("+")
    const digits = pasted.replace(/[^\d]/g, "")

    if (!hasPlus && digits.length <= 11) return

    e.preventDefault()

    const { ddi, number } = parsePhoneWithDDI(pasted)
    if (ddi) {
      const country = resolveCountry(ddi, number, selectedCountry)
      setSelectedCountry(country)
      applyPhoneValue(number, country)
      return
    }

    applyPhoneValue(
      stripAccidentalDialCodePrefix(digits, selectedCountry),
      selectedCountry,
    )
  }

  const input = (
    <Input
      data-testid="phone-input"
      type="tel"
      value={phoneValue}
      onInput={handleInput}
      onPaste={handlePaste}
      aria-invalid={error ? "true" : "false"}
      {...props}
    />
  )

  if (variant === "simple") {
    return input
  }

  return (
    <div className={`flex gap-2 items-start ${className}`}>
      {variant === "default" && (
        <div>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <div className="w-fit">
                <Button
                  data-testid="country-selector"
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between px-3 gap-1"
                  type="button"
                >
                  <span className="flex items-center gap-2 truncate">
                    <span>{selectedCountry.flag}</span>
                    <span>+{selectedCountry.dialCode}</span>
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0">
              <Command>
                <CommandInput
                  data-testid="country-search"
                  placeholder="Buscar país..."
                />
                <CommandList>
                  <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
                  <CommandGroup>
                    {countries.map((country) => (
                      <CommandItem
                        key={country.code}
                        value={phoneCountrySearchValue(country)}
                        onSelect={() => handleCountryChange(country.code)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedCountry.code === country.code
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        <span className="flex items-center gap-2">
                          <span>{country.flag}</span>
                          <span>+{country.dialCode}</span>
                          <span className="text-muted-foreground">
                            {country.name}
                          </span>
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
      {input}
    </div>
  )
}

export type { Country }
export { PhoneInput, parsePhoneWithDDI, formatPhoneNumber, countries }
